import React from 'react';
import { Box, Paper, Typography } from '@mui/material';

const exchangeColors = {
    'UPBIT': {
        bg: '#1772F8',
        color: 'white'
    },
    'BITHUMB': {
        bg: '#FF8A3D',
        color: 'white'
    },
    'BINANCEF': {
        bg: '#F0B90B',
        color: 'black'
    },
    'BYBIT': {
        bg: '#1B2B65',
        color: 'white'
    }
};

const exchangeLogos = {
    'UPBIT': '/images/upbit-logo.png',
    'BITHUMB': '/images/bithumb-logo.png',
    'BINANCEF': '/images/binance-logo.png',
    'BYBIT': '/images/bybit-logo.png'
};

const PortfolioCard = ({ portfolio: { id, pnl, successCount, failedCount, canceledCount, exchange, future_exchange, symbol }, isSelected, onClick }) => {
    const koreaExchangeColor = exchangeColors[exchange?.toUpperCase()] || { bg: 'grey.100', color: 'text.primary' };
    const futureExchangeColor = exchangeColors[future_exchange?.toUpperCase()] || { bg: 'grey.100', color: 'text.primary' };
    const koreaLogo = exchange ? exchangeLogos[exchange.toUpperCase()] : null;
    const futureLogo = future_exchange ? exchangeLogos[future_exchange.toUpperCase()] : null;
    
    return (
        <Paper 
            sx={{ 
                p: 1.5,
                mb: 1,
                cursor: 'pointer',
                border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                '&:hover': {
                    borderColor: '#1976d2'
                }
            }}
            onClick={onClick}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    px: 0.5,
                    py: 0.5,
                    height: '24px',
                }}>
                    <img 
                        src={koreaLogo} 
                        alt={exchange}
                        style={{ 
                            height: '16px',
                            width: 'auto',
                            objectFit: 'contain'
                        }}
                    />
                </Box>
                {future_exchange && (
                    <Box sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        px: 0.5,
                        py: 0.5,
                        height: '24px',
                    }}>
                        <img 
                            src={futureLogo} 
                            alt={future_exchange}
                            style={{ 
                                height: '16px',
                                width: 'auto',
                                objectFit: 'contain'
                            }}
                        />
                    </Box>
                )}
                <Box sx={{ 
                    bgcolor: 'grey.100',
                    color: 'text.secondary',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem'
                }}>
                    {symbol}
                </Box>
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                {id}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography 
                    variant="body2"
                    sx={{ 
                        color: pnl >= 0 ? '#2e7d32' : '#d32f2f',
                        fontWeight: 'medium'
                    }}
                >
                    {pnl.toLocaleString()} KRW
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{ 
                        bgcolor: '#e8f5e9',
                        color: '#2e7d32',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        minWidth: '40px',
                        textAlign: 'center'
                    }}>
                        성공 {successCount}
                    </Box>
                    <Box sx={{ 
                        bgcolor: '#fff3e0',
                        color: '#ed6c02',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        minWidth: '40px',
                        textAlign: 'center'
                    }}>
                        취소 {canceledCount}
                    </Box>
                    <Box sx={{ 
                        bgcolor: '#ffebee',
                        color: '#d32f2f',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        minWidth: '40px',
                        textAlign: 'center'
                    }}>
                        실패 {failedCount}
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
};

const PortfolioList = ({ portfolios, analytics, selectedPortfolio, onPortfolioSelect }) => {
    return (
        <Box sx={{ 
            maxHeight: { xs: 'auto', md: '80vh' },
            overflowY: { xs: 'visible', md: 'auto' },
            overflowX: { xs: 'auto', md: 'hidden' },
            pr: { xs: 0, md: 2 },
            width: '100%'
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                gap: 2,
                pb: { xs: 2, md: 0 },
                minWidth: { xs: 'fit-content', md: '100%' }
            }}>
                {portfolios.map(portfolio => {
                    const { portfolioId: id, orders } = portfolio;
                    
                    const pnl = orders.reduce((total, order) => {
                        return total + (order?.pnl?.total || 0);
                    }, 0);
                    
                    const successCount = orders.filter(order => 
                        order?.korea_order?.execution_result?.status === 'filled' && 
                        (order?.future_order?.execution_result?.status === 'filled' || 
                         (order?.future_order?.execution_result?.status === '30 usdt or less to order'))
                    ).length;

                    const canceledCount = orders.filter(order => 
                        order?.korea_order?.execution_result?.status === 'canceled'
                    ).length;

                    const failedCount = orders.length - successCount - canceledCount;

                    const firstOrder = orders[0];
                    const exchange = firstOrder?.korea_order?.order_info?.exchange;
                    const future_exchange = firstOrder?.future_order?.order_info?.exchange;
                    const symbol = firstOrder?.korea_order?.order_info?.symbol;

                    return {
                        id,
                        pnl,
                        successCount,
                        failedCount,
                        canceledCount,
                        exchange,
                        future_exchange,
                        symbol
                    };
                }).map(portfolio => (
                    <Box sx={{ 
                        minWidth: { xs: '300px', md: '100%' },
                        maxWidth: { xs: '300px', md: '100%' }
                    }} key={portfolio.id}>
                        <PortfolioCard
                            portfolio={portfolio}
                            isSelected={selectedPortfolio === portfolio.id}
                            onClick={() => onPortfolioSelect(portfolio.id)}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default PortfolioList;
